import { useEffect } from "react";
import { FiExternalLink, FiFileText, FiImage, FiX } from "react-icons/fi";

function DocumentViewerModal({ isOpen, onClose, documentItem }) {
  const title = documentItem?.title || "Build Binder Document";
  const fileType = documentItem?.fileType || "";
  const downloadUrl = documentItem?.downloadUrl || "";

  const isPdf = fileType === "application/pdf";
  const isImage = fileType === "image/jpeg" || fileType === "image/png";
  const canPreview = Boolean(downloadUrl) && (isPdf || isImage);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && typeof onClose === "function") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !documentItem) {
    return null;
  }

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && typeof onClose === "function") {
      onClose();
    }
  };

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={handleOverlayClick}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-viewer-title"
        style={panelStyle}
      >
        <header style={headerStyle}>
          <div style={headerTextStyle}>
            <div style={eyebrowRowStyle}>
              {isImage ? (
                <FiImage aria-hidden="true" />
              ) : (
                <FiFileText aria-hidden="true" />
              )}
              <span>Build Binder Preview</span>
            </div>

            <h2 id="document-viewer-title" style={titleStyle}>
              {title}
            </h2>

            <div style={metadataRowStyle}>
              {documentItem.category && (
                <span style={categoryBadgeStyle}>{documentItem.category}</span>
              )}

              {documentItem.fileName && (
                <span style={fileNameStyle}>{documentItem.fileName}</span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close document preview"
            style={closeButtonStyle}
          >
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div style={previewAreaStyle}>
          {canPreview && isPdf && (
            <iframe
              src={downloadUrl}
              title={`Preview of ${title}`}
              style={iframeStyle}
            />
          )}

          {canPreview && isImage && (
            <div style={imageWrapStyle}>
              <img src={downloadUrl} alt={title} style={imageStyle} />
            </div>
          )}

          {!downloadUrl && (
            <FallbackState
              title="Preview unavailable."
              text="This document does not have a valid file URL."
            />
          )}

          {downloadUrl && !isPdf && !isImage && (
            <FallbackState
              title="Preview unavailable for this file type."
              text="You can still open the original file in a new tab."
            />
          )}
        </div>

        <footer style={footerStyle}>
          {downloadUrl ? (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={openLinkStyle}
              aria-label={`Open ${title} in a new tab`}
            >
              <FiExternalLink aria-hidden="true" />
              Open in New Tab
            </a>
          ) : (
            <span style={disabledLinkStyle}>
              <FiExternalLink aria-hidden="true" />
              Open in New Tab
            </span>
          )}

          <button type="button" onClick={handleClose} style={footerCloseStyle}>
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}

function FallbackState({ title, text }) {
  return (
    <div style={fallbackStyle}>
      <div style={fallbackIconStyle}>
        <FiFileText aria-hidden="true" />
      </div>

      <h3 style={fallbackTitleStyle}>{title}</h3>

      <p style={fallbackTextStyle}>{text}</p>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 50000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.88)",
  padding: "12px",
  boxSizing: "border-box"
};

const panelStyle = {
  width: "100%",
  maxWidth: "1080px",
  height: "min(860px, calc(100vh - 24px))",
  maxHeight: "calc(100vh - 24px)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: "#151515",
  border: "1px solid #333",
  borderRadius: "22px",
  boxShadow: "0 28px 90px rgba(0,0,0,0.62)",
  boxSizing: "border-box"
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "14px",
  padding: "18px",
  borderBottom: "1px solid #2b2b2b",
  backgroundColor: "#181818",
  boxSizing: "border-box"
};

const headerTextStyle = {
  minWidth: 0,
  flex: 1
};

const eyebrowRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#e10600",
  fontSize: "12px",
  fontWeight: "900",
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  marginBottom: "8px"
};

const titleStyle = {
  margin: 0,
  color: "#fff",
  fontSize: "22px",
  lineHeight: "1.2",
  fontWeight: "850",
  overflowWrap: "anywhere"
};

const metadataRowStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "10px",
  minWidth: 0
};

const categoryBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "999px",
  padding: "5px 9px",
  backgroundColor: "rgba(225, 6, 0, 0.14)",
  border: "1px solid rgba(225, 6, 0, 0.38)",
  color: "#ffb4b4",
  fontSize: "12px",
  fontWeight: "850"
};

const fileNameStyle = {
  color: "#a8a8a8",
  fontSize: "13px",
  lineHeight: "1.4",
  overflowWrap: "anywhere"
};

const closeButtonStyle = {
  width: "42px",
  height: "42px",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #353535",
  borderRadius: "14px",
  backgroundColor: "#222",
  color: "#fff",
  fontSize: "20px",
  cursor: "pointer"
};

const previewAreaStyle = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  backgroundColor: "#0c0c0c",
  boxSizing: "border-box"
};

const iframeStyle = {
  width: "100%",
  height: "100%",
  minHeight: "520px",
  border: "none",
  display: "block",
  backgroundColor: "#111"
};

const imageWrapStyle = {
  minHeight: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  boxSizing: "border-box"
};

const imageStyle = {
  display: "block",
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  borderRadius: "12px"
};

const fallbackStyle = {
  minHeight: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "28px",
  boxSizing: "border-box"
};

const fallbackIconStyle = {
  width: "62px",
  height: "62px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "20px",
  backgroundColor: "rgba(225, 6, 0, 0.13)",
  color: "#e10600",
  fontSize: "28px",
  marginBottom: "16px"
};

const fallbackTitleStyle = {
  margin: 0,
  color: "#fff",
  fontSize: "22px",
  lineHeight: "1.2",
  fontWeight: "850"
};

const fallbackTextStyle = {
  margin: "10px 0 0",
  color: "#aaa",
  fontSize: "15px",
  lineHeight: "1.55",
  maxWidth: "420px"
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  padding: "14px 18px",
  borderTop: "1px solid #2b2b2b",
  backgroundColor: "#181818",
  boxSizing: "border-box"
};

const openLinkStyle = {
  minHeight: "44px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  borderRadius: "14px",
  padding: "11px 15px",
  backgroundColor: "#e10600",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "850",
  textDecoration: "none",
  boxSizing: "border-box"
};

const disabledLinkStyle = {
  minHeight: "44px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  borderRadius: "14px",
  padding: "11px 15px",
  backgroundColor: "#2a2a2a",
  color: "#777",
  fontSize: "15px",
  fontWeight: "850",
  boxSizing: "border-box",
  cursor: "not-allowed"
};

const footerCloseStyle = {
  minHeight: "44px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #3a3a3a",
  borderRadius: "14px",
  padding: "11px 15px",
  backgroundColor: "#242424",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "850",
  cursor: "pointer",
  boxSizing: "border-box"
};

export default DocumentViewerModal;